import type { ReactNode } from "react";

import { Icon, type IconName } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

/**
 * The pill and separator dot of `JobDetailHeader`'s meta row
 * (features/jobs/components/view/layout/detail-header.tsx), shared so the
 * meeting header's second row reads identically.
 */
export function MetaBadge({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-contrast/5 bg-contrast/4 p-0.5",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function MetaBadgeIcon({ name }: { name: IconName }) {
  return (
    <div className="flex items-center px-0.5">
      <div className="flex size-[13px] shrink-0 items-center justify-center overflow-hidden rounded-[3px] bg-contrast/10">
        <Icon name={name} size={9} className="text-heading opacity-50" />
      </div>
    </div>
  );
}

export function MetaBadgeText({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center px-0.5">
      <span className="max-w-[220px] truncate text-xs font-medium leading-4 text-contrast/75">
        {children}
      </span>
    </div>
  );
}

/** Icon + label pill in one call — the shape every meeting meta pill uses. */
export function MetaPill({
  icon,
  children,
}: {
  icon: IconName;
  children: ReactNode;
}) {
  return (
    <MetaBadge>
      <MetaBadgeIcon name={icon} />
      <MetaBadgeText>{children}</MetaBadgeText>
    </MetaBadge>
  );
}

export function MetaDot() {
  return <div className="size-1 shrink-0 rounded-full bg-contrast/30" />;
}
